import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Autocomplete from "@mui/material/Autocomplete"
import Avatar from "@mui/material/Avatar"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import FormControl from "@mui/material/FormControl"
import InputAdornment from "@mui/material/InputAdornment"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Paper from "@mui/material/Paper"
import Select from "@mui/material/Select"
import Slider from "@mui/material/Slider"
import TextField from "@mui/material/TextField"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import Grid from "@mui/material/Unstable_Grid2"
import { styled } from "@mui/material/styles"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import CloseableSnackbar, {
  useCloseableSnackbarState,
} from "../../../common/components/CloseableSnackbar"
import Column from "../../../common/components/Column"
import Row, { RowButColumnOnMobile } from "../../../common/components/Row"
import TitledSection from "../../../common/components/TitledSection"
import SegmentTracker from "../../../common/tracking"
import { canPurchaseMls } from "../../users/accountRole"
import { selectProfile } from "../../users/reducer"
import {
  INFINITY_SIGN,
  MonthlyDeskFeeTextField,
  PerTransactionFeeTextField,
  RemoveCommissionTierIcon,
  SalesVolumeLimitTextField,
  useCommissionTiersUtils,
} from "../components/offers/CreateOfferDialog"
import {
  selectCRMSettings,
  selectCommissionTiers,
  updateCRMSettings,
} from "../reducer"
import AvatarEditor, { useAvatarEditorState } from "./AvatarEditor"
import PerksSection from "./PerksSection"
import ProfileColumn from "./ProfileColumn"
import { mlsPurchasePath } from "./campaignNavigation"

export default function OfficeTab() {
  const {
    inputElemRef: customBrandImageInputElemRef,
    handleNewImageSelected: handleCustomBrandImageSelected,
    handleEditButtonClicked: handleEditCustomBrandImageButtonClicked,
    handleCropFinished,
    handleCropPopoverClosed,
    croppedImageBlob: croppedCustomBrandImageBlob,
    imageName: customBrandImageName,
    cropPopoverOpen,
    croppedImageDataURL,
    imageDataURL,
    popoverAnchorElemRef,
  } = useAvatarEditorState()
  const [brands, setBrands] = useState([])
  const [selectedBrand, setSelectedBrand] = useState(null)
  const [brandInputValue, setBrandInputValue] = useState("")
  const [marketCommissionRate, setMarketCommissionRate] = useState(2.5)
  const {
    commissionTiers,
    setCommissionTiers,
    handleTierPropertyChanged,
    handleTierDeleted,
    getTiersPayload,
    hoveredCommissionTierRowIndex,
    setHoveredCommissionTierRowIndex,
    limitHasValue,
  } = useCommissionTiersUtils({
    broker_split: 50,
    deal_fee: 500,
    desk_fee: 99,
    limit: INFINITY_SIGN,
  })
  const [brokerTitle, setBrokerTitle] = useState("")
  const [brokerageName, setBrokerageName] = useState("")
  const [isSavingCRMSettings, setIsSavingCRMSettings] = useState(false)
  const navigate = useNavigate()
  const {
    isOpen: successSnackbarOpen,
    message: successSnackbarMessage,
    open: openSuccessSnackbar,
    close: closeSuccessSnackbar,
  } = useCloseableSnackbarState()
  const profile = useSelector(selectProfile)
  const crmSettings = useSelector(selectCRMSettings)
  const commissionSplitTiers = useSelector(selectCommissionTiers)
  const dispatch = useDispatch()
  const axios = useAuthorizedAxiosClient()

  function handleSaveCRMSettingsButtonClicked() {
    setIsSavingCRMSettings(true)

    const formData = new FormData()
    formData.append("market_commission_rate", marketCommissionRate)
    formData.append("commission_split_tiers", JSON.stringify(getTiersPayload()))
    formData.append("broker_title", brokerTitle)
    formData.append("brokerage_name", brokerageName)

    if (selectedBrand) {
      formData.append("brand_write", selectedBrand.id)
    }

    if (croppedCustomBrandImageBlob) {
      formData.append(
        "custom_brand_image",
        croppedCustomBrandImageBlob,
        customBrandImageName
      )
    }

    axios
      .put("/crm/api/offer-settings/", formData)
      .then(({ data }) => {
        dispatch(
          updateCRMSettings(
            Object.fromEntries(
              Object.entries(data).filter((entry) => entry[0] !== "mlss")
            )
          )
        )
        openSuccessSnackbar("Settings updated")
        SegmentTracker.track("Office settings updated", data)
        SegmentTracker.updateProfileFromCRMSettings(profile, data)
      })
      .finally(() => setIsSavingCRMSettings(false))
  }

  useEffect(
    () => {
      if (crmSettings.loading) {
        return
      }

      setMarketCommissionRate(crmSettings.settings.market_commission_rate)
      setCommissionTiers(
        commissionSplitTiers.map((tier) =>
          limitHasValue(tier) ? tier : { ...tier, limit: INFINITY_SIGN }
        )
      )
      setBrokerTitle(crmSettings.settings.broker_title || "")
      setBrokerageName(crmSettings.settings.brokerage_name || "")
      const settingsBrand = crmSettings.settings.brand
      if (settingsBrand) {
        setSelectedBrand(settingsBrand)
        setBrandInputValue(settingsBrand.name)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [crmSettings]
  )

  useEffect(
    () => {
      axios
        .get("/realitydb/api/brand/")
        .then(({ data }) =>
          setBrands(data.sort((b1, b2) => (b1.name === "INDEPENDENT" ? -1 : 1)))
        )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <ProfileColumn>
      <Column
        sx={{
          gap: 3,
        }}
      >
        <TitledSection title="Office settings" variant="h5">
          <Grid container spacing={2}>
            <Grid xs={12} sm={3}>
              <TextField
                label="Broker title"
                value={brokerTitle}
                onChange={(e) => setBrokerTitle(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title="Will be used to fill your broker title in new Email and SMS templates"
                        placement="left"
                      >
                        <InfoOutlinedIcon />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
            </Grid>
            <Grid xs={12} sm={3}>
              <TextField
                label="Brokerage name"
                value={brokerageName}
                onChange={(e) => setBrokerageName(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title="Will be used to fill your brokerage name in new Email and SMS templates"
                        placement="left"
                      >
                        <InfoOutlinedIcon />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
            </Grid>
            <Grid xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Market commission rate</InputLabel>
                <Select
                  label="Market commission rate"
                  value={marketCommissionRate}
                  onChange={(e) => setMarketCommissionRate(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">%</InputAdornment>
                  }
                >
                  <MenuItem value={2.5}>2.5</MenuItem>
                  <MenuItem value={3}>3</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12} sm={6}>
              <Column sx={{ gap: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={600}
                >
                  MLS access
                </Typography>
                {(crmSettings.settings.mlss || []).length > 0 ? (
                  <Row sx={{ flexWrap: "wrap", gap: 0.75 }}>
                    {(crmSettings.settings.mlss || []).map((mls) => (
                      <Chip key={mls.id} label={mls.name} size="small" />
                    ))}
                  </Row>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No MLS on your account yet. Purchase MLS to browse agents in
                    your market.
                  </Typography>
                )}
                {canPurchaseMls(profile) ? (
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ alignSelf: "flex-start" }}
                    onClick={() => navigate(mlsPurchasePath())}
                  >
                    Purchase MLS
                  </Button>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Ask your account owner to purchase MLS access.
                  </Typography>
                )}
              </Column>
            </Grid>
          </Grid>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              position: "relative",
              transition: (theme) =>
                theme.transitions.create(["height"], { duration: 1000 }),
              mt: 1,
            }}
          >
            <TiersLabel>Commission Tiers</TiersLabel>
            <Column sx={{ gap: 1, p: 2 }}>
              {commissionTiers.map((commissionTier, i) => (
                <Column sx={{ gap: 1 }} key={i}>
                  <Typography variant="h5">Tier {i + 1}</Typography>
                  <Grid
                    container
                    spacing={2}
                    sx={{ position: "relative" }}
                    onMouseEnter={() => setHoveredCommissionTierRowIndex(i)}
                    onMouseLeave={() => setHoveredCommissionTierRowIndex(null)}
                  >
                    <CenterVerticalGridItem xs={4} sm={1.1}>
                      <SliderLabelRow>
                        <SliderLabel>Broker Split</SliderLabel>
                        <SliderValue>
                          {commissionTier.broker_split}%
                        </SliderValue>
                      </SliderLabelRow>
                    </CenterVerticalGridItem>
                    <CenterVerticalGridItem xs={4} sm={2}>
                      <Slider
                        value={commissionTier.broker_split}
                        onChange={(e, newValue) =>
                          handleTierPropertyChanged(i, "broker_split", newValue)
                        }
                      />
                    </CenterVerticalGridItem>
                    <CenterVerticalGridItem xs={4} sm={1.1}>
                      <SliderLabelRow>
                        <SliderValue>
                          {100 - commissionTier.broker_split}%
                        </SliderValue>
                        <SliderLabel>Agent Split</SliderLabel>
                      </SliderLabelRow>
                    </CenterVerticalGridItem>
                    <CenterVerticalGridItem xs={6} sm={2}>
                      <PerTransactionFeeTextField
                        value={commissionTier.deal_fee}
                        onChange={(e) =>
                          handleTierPropertyChanged(
                            i,
                            "deal_fee",
                            e.target.value
                          )
                        }
                      />
                    </CenterVerticalGridItem>
                    <CenterVerticalGridItem xs={6} sm={2}>
                      <MonthlyDeskFeeTextField
                        value={commissionTier.desk_fee}
                        onChange={(e) =>
                          handleTierPropertyChanged(
                            i,
                            "desk_fee",
                            e.target.value
                          )
                        }
                      />
                    </CenterVerticalGridItem>
                    <CenterVerticalGridItem xs={6} sm={1.8}>
                      <Column>
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          fontSize="0.9em"
                          lineHeight={1}
                        >
                          From
                        </Typography>
                        <Typography variant="h5">
                          $
                          {parseInt(
                            commissionTiers[i - 1]?.limit || 0
                          ).toLocaleString()}
                        </Typography>
                      </Column>
                    </CenterVerticalGridItem>
                    <CenterVerticalGridItem xs={6} sm={1.8}>
                      <SalesVolumeLimitTextField
                        value={commissionTier.limit}
                        onChange={(e) =>
                          handleTierPropertyChanged(i, "limit", e.target.value)
                        }
                        error={!!commissionTier.limit_error}
                        helperText={commissionTier.limit_error}
                      />
                    </CenterVerticalGridItem>
                    <RemoveCommissionTierIcon
                      index={i}
                      hoveredCommissionTierRowIndex={
                        hoveredCommissionTierRowIndex
                      }
                      commissionTiers={commissionTiers}
                      handleTierDeleted={handleTierDeleted}
                      right={4}
                    />
                  </Grid>
                </Column>
              ))}
            </Column>
          </Paper>
        </TitledSection>
        <TitledSection variant="h5" title="Brand">
          <RowButColumnOnMobile sx={{ alignItems: "center", gap: 4 }}>
            <AvatarEditor
              cropPopoverOpen={cropPopoverOpen}
              croppedImageDataURL={croppedImageDataURL}
              handleCropFinished={handleCropFinished}
              handleCropPopoverClosed={handleCropPopoverClosed}
              imageDataURL={imageDataURL}
              inputElemRef={customBrandImageInputElemRef}
              onEditButtonClicked={handleEditCustomBrandImageButtonClicked}
              onNewImageSelected={handleCustomBrandImageSelected}
              popoverAnchorElemRef={popoverAnchorElemRef}
            >
              <Avatar
                src={
                  crmSettings.settings.custom_brand_image || selectedBrand?.icon
                }
                sx={{ width: "120px", height: "120px" }}
              />
            </AvatarEditor>
            <Autocomplete
              value={selectedBrand}
              onChange={(_, newBrand) => setSelectedBrand(newBrand)}
              inputValue={brandInputValue}
              onInputChange={(_, newValue) => setBrandInputValue(newValue)}
              options={brands}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: selectedBrand ? (
                      <InputAdornment position="start" sx={{ mr: 0, ml: 0.5 }}>
                        <Avatar
                          src={selectedBrand.icon}
                          sx={{ width: "24px", height: "24px" }}
                        />
                      </InputAdornment>
                    ) : null,
                  }}
                  label="Brand"
                  placeholder="Brand"
                  fullWidth
                />
              )}
              renderOption={(props, option) => (
                <BrandOption option={option} {...props} />
              )}
              sx={{ minWidth: { xs: "100%", sm: "300px" } }}
            />
          </RowButColumnOnMobile>
        </TitledSection>
        <PerksSection />
        <LoadingButton
          variant="contained"
          loading={isSavingCRMSettings}
          sx={{ alignSelf: "flex-start" }}
          onClick={handleSaveCRMSettingsButtonClicked}
        >
          Save changes
        </LoadingButton>
      </Column>
      <CloseableSnackbar
        open={successSnackbarOpen}
        onClose={closeSuccessSnackbar}
        message={successSnackbarMessage}
      />
    </ProfileColumn>
  )
}

function BrandOption({ option, ...props }) {
  return (
    <Row
      component="li"
      {...props}
      sx={{
        alignItems: "center",
        gap: 1,
      }}
    >
      <Avatar src={option.icon} sx={{ width: "32px", height: "32px" }} />
      <Typography variant="body1">{option.name}</Typography>
    </Row>
  )
}

const TiersLabel = styled((props) => <Typography variant="body1" {...props} />)(
  ({ theme }) => ({
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: "16px",
  })
)

const SliderLabelRow = styled(Row)(({ theme }) => ({
  lignItems: "center",
  gap: theme.spacing(1),
}))

const SliderLabel = styled((props) => (
  <Typography variant="body1" {...props} />
))(({ theme }) => ({
  color: theme.palette.text.secondary,
}))

const SliderValue = styled((props) => (
  <Typography variant="body1" {...props} />
))(({ theme }) => ({
  fontWeight: 600,
}))

const CenterVerticalGridItem = styled((props) => (
  <Grid display="flex" alignItems="center" {...props} />
))(() => ({}))
